import React, { useState, useEffect, useRef, createRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

// leaflet
import L from "leaflet";
import "leaflet-contextmenu";
import { MapContainer, TileLayer, ZoomControl, Marker, useMap } from "react-leaflet";

// components
import CustomLayerControl, { LayerControlItem } from "./LayerControl";

// hooks and utilities
import { getModelData, getShorthandModelName } from "hooks/useModelData";
import { getColorByAircraftStatus } from "hooks/useColors";
import { routerOptions } from "hooks/useRedirect";
import { useTabIdNew } from "hooks/useTabId";
import { useLanguage } from "lang/Translate";
import { useHub } from "hooks/useAirport";
import useFleet from "hooks/useFleet";

//css
import "./Map.scss";

// assets
import bluePin from "assets/images/map/pins/pinBlue.svg";
import darkBluePin from "assets/images/map/pins/pinDarkBlue.svg";
import map1 from "assets/images/map/thumbnails/default.jpg";
import map2 from "assets/images/map/thumbnails/light.jpg";
import map3 from "assets/images/map/thumbnails/dark.jpg";
import map4 from "assets/images/map/thumbnails/satellite.jpg";
import useEventListener from "hooks/useEventListener";

const Map = () => {
	const [contextMenuItems, setContextMenuItems] = useState([]);
	const hubMarkerRef = useRef();
	const lang = useLanguage();
	const hub = useHub();

	const fleetMap = useFleet();
	const fleet = useMemo(() => [...fleetMap.values()], [fleetMap]);

	const arrLength = 6;
	const elRefs = useRef([]);
	if (elRefs.current.length !== arrLength) {
		elRefs.current = Array(arrLength)
			.fill()
			.map((_, i) => elRefs.current[i] || createRef());
	}

	useEffect(() => {
		const aircraftInHub = fleet.filter(a => a.location === hub.id);
		if (aircraftInHub.length <= 0) {
			setContextMenuItems([]);
			return;
		}

		setContextMenuItems([
			{
				text: hub?.iata_code || hub?.ident,
				index: 0,
				hideOnSelect: false
			},
			...aircraftInHub.map((a, i) => {
				const plane = getModelData(a.id);

				return {
					text: `<span><b>${getShorthandModelName(plane.name)}</b><span>${a.registration}</span></span>`,
					index: i + 1,
					iconCls: `fas fa-plane ${getColorByAircraftStatus(a.status, a.grounded)[0]}`,
					callback: () => {
						document.dispatchEvent(new CustomEvent("goToAircraftOnMap", { detail: { ident: a.ident } }));
					}
				};
			})
		]);
	}, [hub, fleet]);

	useEffect(() => {
		if (!hubMarkerRef.current) return;
		const hubMarker = hubMarkerRef.current;

		if (contextMenuItems.length <= 0) {
			hubMarker.unbindContextMenu();
			return;
		}

		const contextMenu = hubMarker.bindContextMenu({
			contextmenu: true,
			contextmenuInheritItems: false,
			contextmenuItems: contextMenuItems
		});

		const callback = e => {
			var itemOptions;
			if (contextMenu._map.contextmenu) {
				const data = L.extend({ relatedTarget: contextMenu }, e);

				if (!contextMenu.options.contextmenuInheritItems) {
					window.map.contextmenu.hideAllItems();
				}

				for (let i = 0, l = contextMenu.options.contextmenuItems.length; i < l; i++) {
					itemOptions = contextMenu.options.contextmenuItems[i];
					contextMenu._items.push(window.map.contextmenu.insertItem(itemOptions, itemOptions.index));
				}

				window.map.once("contextmenu.hide", contextMenu._hideContextMenu, contextMenu);
				window.map.contextmenu.showAt(e.latlng, data);
			}
		};

		hubMarker.addEventListener("click", callback);
		return () => document.removeEventListener("click", callback);
	}, [contextMenuItems]);

	if (!hub) return;
	return (
		<MapContainer
			center={[hub.latitude_deg, hub.longitude_deg]}
			minZoom={3}
			zoom={3}
			maxZoom={12}
			scrollWheelZoom={true}
			zoomControl={false}
			maxBounds={[[-90.0, hub.longitude_deg - 180], [90.0, hub.longitude_deg + 180]]}
			// maxBounds={[[-90.0, -180.0], [90.0, 180.0]]}
			maxBoundsViscosity={1}
			attribution={false}
			edgeBufferTiles={3}
			contextmenu={true}
			className="map"
			whenReady={() => document.dispatchEvent(new CustomEvent("map_loaded"))}
		>
			<ZoomControl position="bottomleft" />
			<ZoomAnim latitude={hub.latitude_deg} longitude={hub.longitude_deg} zoom={8} delay={1000} />
			<Attribution />

			<CustomLayerControl refs={elRefs}>
				<LayerControlItem
					checked={true}
					name={lang === "PL" ? "Domyślna" : "Default"}
					group="BaseLayer"
					id="1"
					img={map1}
				>
					<TileLayer
						attribution="Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012"
						url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
						noWrap={false}
						ref={elRefs.current[1]}
					/>
				</LayerControlItem>

				<LayerControlItem
					checked={false}
					name={lang === "PL" ? "Jasna" : "Light"}
					group="BaseLayer"
					id="2"
					img={map2}
					filter={true}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
						url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
						noWrap={false}
						ref={elRefs.current[3]}
					/>
				</LayerControlItem>

				<LayerControlItem
					checked={false}
					name={lang === "PL" ? "Ciemna" : "Dark"}
					group="BaseLayer"
					id="3"
					img={map3}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
						url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
						noWrap={false}
						ref={elRefs.current[4]}
					/>
				</LayerControlItem>

				<LayerControlItem
					checked={false}
					name={lang === "PL" ? "Satelitarna" : "Satellite"}
					group="BaseLayer"
					id="4"
					img={map4}
				>
					<TileLayer
						attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
						url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
						noWrap={false}
						ref={elRefs.current[5]}
					/>
				</LayerControlItem>
			</CustomLayerControl>

			{/* <Marker position={[latitude, longitude]} zIndexOffset={1000} icon={hubClicked ? hubIconDark : hubIcon} ref={hubMarkerRef} /> */}
			<HubMarker longitude={hub.longitude_deg} latitude={hub.latitude_deg} />
			<MapController />
		</MapContainer>
	);
};

function HubMarker({ latitude, longitude }) {
	const [hubClicked, setHubClicked] = useState(false);
	const airportInSideWindow = useTabIdNew("airport", { isNumber: true, ...routerOptions.get("s") });

	const hub = useHub();
	const routes = useSelector(state => state.routes);
	const dispatch = useDispatch();

	const hubIcon = L.icon({
		iconUrl: bluePin,
		iconSize: [38, 38],
		iconAnchor: [19, 38]
	});

	const hubIconDark = L.icon({
		iconUrl: darkBluePin,
		iconSize: [38, 38],
		iconAnchor: [19, 38]
	});

	function handleHubClick() {
		if (!hubClicked) {
			routes.forEach(route => route.id !== this.id && (route.pinClicked = false));
			dispatch({ type: "SET_SIDE_WINDOW_ADDRESS", state: `airport~${hub.id}` });
		} else {
			dispatch({ type: "SET_SIDE_WINDOW_ADDRESS", state: "" });
		}
	}

	useEffect(() => {
		setHubClicked(airportInSideWindow === hub.id);
	}, [airportInSideWindow, hub.id]);

	return (
		<Marker
			position={[latitude, longitude]}
			zIndexOffset={1000}
			icon={hubClicked ? hubIconDark : hubIcon}
			eventHandlers={{ click: handleHubClick }}
		/>
	);
}

const Attribution = () => {
	const map = useMap();
	map.attributionControl.setPosition("bottomleft");
	return null;
};

const ZoomAnim = ({ latitude = 0, longitude = 0, zoom = 11, delay = 900 }) => {
	const map = useMap();
	useEffect(() => {
		setTimeout(() => map.flyTo([latitude, longitude], zoom), delay);
		// L.Polyline.Arc([0, 0], [30, -200], { vertices: 1000 }).addTo(map);
	}, [map, latitude, longitude, zoom, delay]);

	return null;
};

const MapController = () => {
	const aircraft = useTabIdNew("aircraft", { ...routerOptions.get("s"), isNumber: true });
	const map = useMap();

	useEffect(() => {
		window.map = map;
	}, [map]);

	useEventListener(
		document,
		"goToAircraftOnMap",
		useCallback(
			e => {
				const aircraftMarker = Object.values(map._layers).filter(
					layer => layer.options.aircraftIdent === e.detail.ident
				)[0];

				const zoom = map.getZoom();
				map.flyTo(aircraftMarker._latlng, zoom > 12 ? zoom : 12, {
					animate: true,
					duration: 1
				});

				if (aircraft !== e.detail.ident) aircraftMarker.fire("click");
			},
			[aircraft, map]
		)
	);

	return null;
};

export default Map;
